import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import Dashboard from './Dashboard/Dashboard';
import MainLayout from './Layouts/MainLayout';
import { AuthProvider, useAuth } from './stateManagers/services/AuthContext';
import { LoadingScreen } from './components/LoadingScreen';
import LoginScreen from './Layouts/Authorisation/LoginScreen';
import Branches from './Layouts/Branches/Branches';
import BranchDetails from './Layouts/Branches/BranchDetails';
import { getPathForRoute } from './config/routeConfig';
import AddStudent from './Layouts/Students/AddStudent';
import Students from './Layouts/Students/Students';
import Batches from './Layouts/Batches/Batches';
import NoticeBoard from './Layouts/NoticeBoard/NoticeBoard';
import Teachers from './Layouts/Teachers/Teachers';
import AddTeacher from './Layouts/Teachers/AddTeacher';
import StudyMaterials from './Layouts/Studymaterials/StudyMaterials';
import StudentDetails from './Layouts/Students/StudentDetails';
import EditStudent from './Layouts/Students/EditStudent';
import TeacherDetails from './Layouts/Teachers/Teacherdetails';
import EditTeacher from './Layouts/Teachers/EditTeacher';
import StudentsFees from './Layouts/Fees/StudentsFees';
import TeacherPermissionsSettings from './Layouts/Settings/TeacherPermissionsSettings';
import BatchDetails from './Layouts/Batches/BatchDetails';
import TakeExam from './Layouts/Exams/TakeExam';
import CreateClassTest from './Layouts/Exams/CreateClassTest';
import ClassTestList from './Layouts/Exams/ClassTestList';
import ViewClassTest from './Layouts/Exams/ViewClassTest';
import Assignments from './Layouts/Assignments/Assignments';
import ViewAssignment from './Layouts/Assignments/ViewAssignment';
import StudentProfile from './Layouts/MyProfile/StudentProfile';
import AnnouncementsList from './Layouts/Announcements/AnnouncementsList';
import Attendance from './Layouts/Attendance/Attendance';
import StudentPracticalMaterials from './Layouts/PracticalMaterials/StudentPracticalMaterials';

const AppContent: React.FC = () => {
  const { isAuthenticated, user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [contextData, setContextData] = useState<Record<string, any>>({});

  const handleNavigation = (routeId: string, params?: Record<string, string>, data?: Record<string, any>) => {
    if (data) setContextData(prev => ({ ...prev, ...data }));
    navigate(getPathForRoute(routeId, params));
  };

  const handleNavigateTo = (path: string, data?: Record<string, any>) => {
    if (data) setContextData(prev => ({ ...prev, ...data }));
    navigate(path);
  };

  const handleNavigateBack = () => {
    setContextData({});
    navigate(-1);
  };

  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleLogoutCancel = () => setShowLogoutModal(false);
  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setShowLogoutModal(false);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <LoginScreen />;

  return (
    <>
      <MainLayout
        user={user}
        currentPath={location.pathname}
        onNavigate={handleNavigation}
        onLogout={handleLogoutClick}
        contextData={contextData}
      >
        <Routes>
          {/* ── Core ── */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<StudentProfile />} />

          {/* ── Branches ── */}
          <Route
            path="/branches"
            element={<Branches onBranchClick={(id) => handleNavigateTo(`/branches/${id}`)} />}
          />
          <Route
            path="/branches/:branchId"
            element={
              <BranchDetailsWrapper
                onBack={handleNavigateBack}
                onBranchLoad={(name) => setContextData(prev => ({ ...prev, branchName: name }))}
              />
            }
          />

          <Route path="/practical-materials" element={<StudentPracticalMaterials />} />

          <Route
            path="/attendance"
            element={<Attendance />}
          />


          <Route path="/announcements">
            <Route index element={<AnnouncementsList />} />
          </Route>

          {/* ── Students ── */}
          <Route path="/students">
            <Route index element={<Students />} />
            <Route path="add-student" element={<AddStudent />} />
            <Route path="edit/:studentId" element={<EditStudent />} />
            <Route path=":studentId" element={<StudentDetails />} />
          </Route>

          {/* ── Teachers ── */}
          <Route path="/teachers">
            <Route index element={<Teachers />} />
            <Route path="add-teacher" element={<AddTeacher />} />
            <Route path="edit/:teacherId" element={<EditTeacher />} />
            <Route path=":teacherId" element={<TeacherDetails />} />
          </Route>

          {/* ── Batches ── */}
          <Route path="/batches" element={<Batches />} />
          <Route path="/batches/:batchId/details" element={<BatchDetails />} />

          {/* ── Assignments — static before dynamic ── */}
          <Route path="/assignments">
            <Route index element={<Assignments />} />
            <Route path=":id" element={<ViewAssignment />} />
          </Route>

          {/* ── Exams — static before dynamic ── */}
          <Route path="/exams">
            <Route index element={<ClassTestList />} />
            <Route path="class-tests" element={<ClassTestList />} />
            <Route path="class-tests/create" element={<CreateClassTest />} />
            <Route path="class-tests/:id/edit" element={<CreateClassTest />} />
            <Route path="class-tests/:id/take" element={<TakeExam />} />
            <Route path="class-tests/:id" element={<ViewClassTest />} />
          </Route>

          {/* ── Other ── */}
          <Route path="/study-materials" element={<StudyMaterials />} />
          <Route path="/noticeboard" element={<NoticeBoard />} />
          <Route path="/students-fees" element={<StudentsFees />} />
          <Route path="/settings/teacher-permissions" element={<TeacherPermissionsSettings />} />
          <Route path="/school-setup/database" element={<div style={{ padding: '40px' }}><h2>Database Setup</h2></div>} />

          <Route path="*" element={
            <div style={{ padding: '40px' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: '600', color: '#1a1a1a', marginBottom: '16px' }}>
                Welcome, {user?.email}!
              </h2>
              <p style={{ color: '#666', fontSize: '1rem' }}>
                Select a menu item from the sidebar to get started.
              </p>
            </div>
          } />
        </Routes>
      </MainLayout>

      {showLogoutModal && (
        <LogoutModal onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
      )}
    </>
  );
};

// ─── Logout Modal ─────────────────────────────────────────────────────────────

const LogoutModal: React.FC<{ onConfirm: () => void; onCancel: () => void }> = ({ onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
    <div style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
      <div style={{ width: '56px', height: '56px', background: '#fff3cd', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
        <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.5rem', color: '#856404' }} />
      </div>
      <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px 0' }}>Confirm Logout</h3>
      <p style={{ fontSize: '1rem', color: '#666', margin: '0 0 24px 0', lineHeight: '1.5' }}>
        Are you sure you want to logout? You'll need to login again to access your dashboard.
      </p>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={onCancel}
          style={{ flex: 1, padding: '14px 24px', background: '#f5f5f5', color: '#333', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = '#e0e0e0'}
          onMouseLeave={e => e.currentTarget.style.background = '#f5f5f5'}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{ flex: 1, padding: '14px 24px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = '#c82333'}
          onMouseLeave={e => e.currentTarget.style.background = '#dc3545'}
        >
          Logout
        </button>
      </div>
    </div>
  </div>
);

// ─── Branch wrapper ───────────────────────────────────────────────────────────

const BranchDetailsWrapper: React.FC<{
  onBack: () => void;
  onBranchLoad: (name: string) => void;
}> = ({ onBack, onBranchLoad }) => {
  const { branchId } = useParams<{ branchId: string }>();
  if (!branchId) return null;
  return <BranchDetails branchId={branchId} onBack={onBack} onBranchLoad={onBranchLoad} />;
};

// ─── Root ─────────────────────────────────────────────────────────────────────

const App: React.FC = () => (
  <AuthProvider>
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  </AuthProvider>
);

export default App;